import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { listDiscoveryComments, postDiscoveryComment, postGuestComment, deleteMyComment, type DiscoveryComment } from "@/lib/comments.functions";
import { Avatar } from "@/components/Avatar";
import { ARCHETYPES } from "@/lib/archetypes";
import { toast } from "sonner";
import { MessageCircle, Trash2 } from "lucide-react";

const NAME_KEY = "sa.guest.name";

function timeAgo(iso: string) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 2592000) return `${Math.floor(s / 86400)}d`;
  return new Date(iso).toLocaleDateString();
}

export function DiscoveryComments({ discoveryId }: { discoveryId: string }) {
  const list = useServerFn(listDiscoveryComments);
  const post = useServerFn(postDiscoveryComment);
  const postGuest = useServerFn(postGuestComment);
  const del = useServerFn(deleteMyComment);
  const [items, setItems] = useState<DiscoveryComment[]>([]);
  const [body, setBody] = useState("");
  const [name, setName] = useState("");
  const [me, setMe] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setMe(data.user?.id ?? null));
    try { setName(localStorage.getItem(NAME_KEY) ?? ""); } catch { /* ignore */ }
    list({ data: { discoveryId, limit: 50 } }).then((r) => {
      setItems(r.items);
      setLoaded(true);
    });
  }, [discoveryId, list]);

  async function refresh() {
    const r = await list({ data: { discoveryId, limit: 50 } });
    setItems(r.items);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const text = body.trim();
    if (text.length < 2) return;
    const who = name.trim();
    if (!me && who.length < 2) {
      toast.error("Add a name first - anything you like.");
      return;
    }
    setSubmitting(true);
    const res = me
      ? await post({ data: { discoveryId, body: text } })
      : await postGuest({ data: { discoveryId, name: who, body: text } });
    setSubmitting(false);
    if ((res as any)?.ok) {
      if (!me) { try { localStorage.setItem(NAME_KEY, who); } catch { /* ignore */ } }
      setBody("");
      await refresh();
    } else {
      toast.error((res as any)?.error ?? "Could not post");
    }
  }

  async function onDelete(id: string) {
    const res = await del({ data: { commentId: id } });
    if ((res as any)?.ok) setItems((prev) => prev.filter((c) => c.id !== id));
    else toast.error("Could not delete");
  }

  return (
    <section id="comments" className="mx-auto mt-12 max-w-3xl border-t border-border pt-10">
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
        <MessageCircle className="h-3.5 w-3.5" /> Community
      </div>
      <h2 className="mt-2 font-display text-3xl uppercase">Discussion <span className="text-muted-foreground">({items.length})</span></h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Add your name and join in. No account needed.
      </p>


      <form onSubmit={onSubmit} className="mt-5 rounded-2xl border border-border bg-card p-4">
        {!me && (
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name (or a nickname)"
            maxLength={32}
            className="mb-2 w-full rounded-xl border border-border bg-background p-3 text-sm"
            style={{ fontSize: 16 }}
          />
        )}
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Add a thought, a question or a fact…"
          maxLength={2000}
          rows={3}
          className="w-full resize-none rounded-xl border border-border bg-background p-3 text-sm"
          style={{ fontSize: 16 }}
        />
        <div className="mt-2 flex items-center justify-between gap-3">
          <span className="text-[10px] text-muted-foreground">
            {me ? `${body.length}/2000` : <>Posting as a guest · <Link to="/auth" className="font-bold uppercase tracking-widest text-primary">sign in</Link> to keep a profile</>}
          </span>
          <button type="submit" disabled={submitting || body.trim().length < 2} className="rounded-full bg-primary px-5 py-2 text-[11px] font-bold uppercase tracking-widest text-primary-foreground hover:brightness-110 disabled:opacity-50">
            {submitting ? "Posting…" : "Post"}
          </button>
        </div>
      </form>

      <div className="mt-6 space-y-4">
        {!loaded && <p className="text-sm text-muted-foreground">Loading…</p>}
        {loaded && items.length === 0 && (
          <p className="text-sm text-muted-foreground">No comments yet. Start the discussion.</p>
        )}
        {items.map((c) => {
          const arch = c.author?.archetype ? ARCHETYPES[c.author.archetype as keyof typeof ARCHETYPES] : null;
          const handle = c.author?.username;
          const name = c.author?.display_name || handle || c.guest_name || "Anonymous";
          return (
            <article key={c.id} className="rounded-2xl border border-border bg-card p-4">
              <div className="flex items-start gap-3">
                <Avatar seed={c.author?.avatar_seed ?? c.user_id ?? c.guest_name ?? c.id} style={c.author?.avatar_style} size={40} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    {handle ? (
                      <Link to="/u/$username" params={{ username: handle }} className="font-bold hover:text-primary">{name}</Link>
                    ) : (
                      <span className="font-bold">{name}</span>
                    )}
                    {arch && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-primary">{arch.emoji} {arch.name.replace("The ","")}</span>}
                    <span className="text-muted-foreground">· {timeAgo(c.created_at)}</span>
                  </div>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed">{c.body}</p>
                </div>
                {me && me === c.user_id && (
                  <button onClick={() => onDelete(c.id)} aria-label="Delete" className="text-muted-foreground transition hover:text-destructive">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

